/**
 * Short public-domain passages for the Random Paragraph Generator.
 *
 * Every passage is a paragraph (or the stated part of one) from a work first published in
 * the United States before 1931, so it is in the US public domain. Each was copied from
 * the source named in its credit and checked against that source word for word. Only
 * typography is normalised: quotation marks and apostrophes are straight, a double hyphen
 * is an em dash, and the underscores that mark italics in plain-text editions are
 * dropped. Spelling, punctuation and word choice are the source's own, which is why
 * "to-day" and "tranquillity" appear.
 *
 * `excerpt` is true when the passage is part of its paragraph (a sentence or more, cut at
 * a sentence end) rather than the whole of it.
 */

export const TOPICS = ["nature", "stories", "ideas", "history"] as const;
export type Topic = (typeof TOPICS)[number];

export interface Passage {
  id: string;
  topic: Topic;
  author: string;
  work: string;
  /** First US publication of the work (for a speech, the year it was given). */
  year: number;
  excerpt: boolean;
  /** Where the text was copied from, e.g. "Project Gutenberg #205". */
  source: string;
  url: string;
  text: string;
}

export const PASSAGES: readonly Passage[] = [
  {
    id: "walden-solitude",
    topic: "nature",
    author: "Henry David Thoreau",
    work: "Walden",
    year: 1854,
    excerpt: false,
    source: "Project Gutenberg #205",
    url: "https://www.gutenberg.org/ebooks/205",
    text: "I find it wholesome to be alone the greater part of the time. To be in company, even with the best, is soon wearisome and dissipating. I love to be alone. I never found the companion that was so companionable as solitude.",
  },
  {
    id: "emerson-eyeball",
    topic: "nature",
    author: "Ralph Waldo Emerson",
    work: "Nature",
    year: 1836,
    excerpt: true,
    source: "Project Gutenberg #29433",
    url: "https://www.gutenberg.org/ebooks/29433",
    text: "Standing on the bare ground,—my head bathed by the blithe air, and uplifted into infinite space,—all mean egotism vanishes. I become a transparent eye-ball; I am nothing; I see all; the currents of the Universal Being circulate through me; I am part or particle of God.",
  },
  {
    id: "call-of-the-wild",
    topic: "nature",
    author: "Jack London",
    work: "The Call of the Wild",
    year: 1903,
    excerpt: true,
    source: "Project Gutenberg #215",
    url: "https://www.gutenberg.org/ebooks/215",
    text: "Buck did not read the newspapers, or he would have known that trouble was brewing, not alone for himself, but for every tide-water dog, strong of muscle and with warm, long hair, from Puget Sound to San Diego. Because men, groping in the Arctic darkness, had found a yellow metal, and because steamship and transportation companies were booming the find, thousands of men were rushing into the Northland.",
  },
  {
    id: "sleepy-hollow",
    topic: "nature",
    author: "Washington Irving",
    work: "The Legend of Sleepy Hollow",
    year: 1820,
    excerpt: true,
    source: "Project Gutenberg #41",
    url: "https://www.gutenberg.org/ebooks/41",
    text: "Not far from this village, perhaps about two miles, there is a little valley or rather lap of land among high hills, which is one of the quietest places in the whole world. A small brook glides through it, with just murmur enough to lull one to repose; and the occasional whistle of a quail or tapping of a woodpecker is almost the only sound that ever breaks in upon the uniform tranquillity.",
  },
  {
    id: "muir-june13",
    topic: "nature",
    author: "John Muir",
    work: "My First Summer in the Sierra",
    year: 1911,
    excerpt: false,
    source: "Project Gutenberg #32540",
    url: "https://www.gutenberg.org/ebooks/32540",
    text: "June 13. Another glorious Sierra day in which one seems to be dissolved and absorbed and sent pulsing onward we know not where. Life seems neither long nor short, and we take no more heed to save time or make haste than do the trees and stars. This is true freedom, a good practical sort of immortality. Yonder rises another white skyland. How sharply the yellow pine spires and the palm-like crowns of the sugar pines are outlined on its smooth white domes. And hark! the grand thunder billows booming, rolling from ridge to ridge, followed by the faithful shower.",
  },
  {
    id: "muir-raindrops",
    topic: "nature",
    author: "John Muir",
    work: "My First Summer in the Sierra",
    year: 1911,
    excerpt: false,
    source: "Project Gutenberg #32540",
    url: "https://www.gutenberg.org/ebooks/32540",
    text: "Now the storm is over, the sky is clear, the last rolling thunder-wave is spent on the peaks, and where are the raindrops now—what has become of all the shining throng? In winged vapor rising some are already hastening back to the sky, some have gone into the plants, creeping through invisible doors into the round rooms of cells, some are locked in crystals of ice, some in rock crystals, some in porous moraines to keep their small springs flowing, some have gone journeying on in the rivers to join the larger raindrop of the ocean. From form to form, beauty to beauty, ever changing, never resting, all are speeding on with love's enthusiasm, singing with the stars the eternal song of creation.",
  },
  {
    id: "muir-daybreak",
    topic: "nature",
    author: "John Muir",
    work: "My First Summer in the Sierra",
    year: 1911,
    excerpt: false,
    source: "Project Gutenberg #32540",
    url: "https://www.gutenberg.org/ebooks/32540",
    text: "July 19. Watching the daybreak and sunrise. The pale rose and purple sky changing softly to daffodil yellow and white, sunbeams pouring through the passes between the peaks and over the Yosemite domes, making their edges burn; the silver firs in the middle ground catching the glow on their spiry tops, and our camp grove fills and thrills with the glorious light. Everything awakening alert and joyful; the birds begin to stir and innumerable insect people. Deer quietly withdraw into leafy hiding-places in the chaparral; the dew vanishes, flowers spread their petals, every pulse beats high, every life cell rejoices, the very rocks seem to thrill with life. The whole landscape glows like a human face in a glory of enthusiasm, and the blue sky, pale around the horizon, bends peacefully down over all like one vast flower.",
  },
  {
    id: "moby-dick",
    topic: "stories",
    author: "Herman Melville",
    work: "Moby-Dick",
    year: 1851,
    excerpt: true,
    source: "Project Gutenberg #2701",
    url: "https://www.gutenberg.org/ebooks/2701",
    text: "Call me Ishmael. Some years ago—never mind how long precisely—having little or no money in my purse, and nothing particular to interest me on shore, I thought I would sail about a little and see the watery part of the world. It is a way I have of driving off the spleen and regulating the circulation. Whenever I find myself growing grim about the mouth; whenever it is a damp, drizzly November in my soul; whenever I find myself involuntarily pausing before coffin warehouses, and bringing up the rear of every funeral I meet; and especially whenever my hypos get such an upper hand of me, that it requires a strong moral principle to prevent me from deliberately stepping into the street, and methodically knocking people's hats off—then, I account it high time to get to sea as soon as I can.",
  },
  {
    id: "huck-finn",
    topic: "stories",
    author: "Mark Twain",
    work: "Adventures of Huckleberry Finn",
    year: 1885,
    excerpt: true,
    source: "Project Gutenberg #76",
    url: "https://www.gutenberg.org/ebooks/76",
    text: "You don't know about me without you have read a book by the name of The Adventures of Tom Sawyer; but that ain't no matter. That book was made by Mr. Mark Twain, and he told the truth, mainly. There was things which he stretched, but mainly he told the truth. That is nothing. I never seen anybody but lied one time or another, without it was Aunt Polly, or the widow, or maybe Mary. Aunt Polly—Tom's Aunt Polly, she is—and Mary, and the Widow Douglas is all told about in that book, which is mostly a true book, with some stretchers, as I said before.",
  },
  {
    id: "oz",
    topic: "stories",
    author: "L. Frank Baum",
    work: "The Wonderful Wizard of Oz",
    year: 1900,
    excerpt: true,
    source: "Project Gutenberg #55",
    url: "https://www.gutenberg.org/ebooks/55",
    text: "Dorothy lived in the midst of the great Kansas prairies, with Uncle Henry, who was a farmer, and Aunt Em, who was the farmer's wife. Their house was small, for the lumber to build it had to be carried by wagon many miles. There were four walls, a floor and a roof, which made one room; and this room contained a rusty looking cookstove, a cupboard for the dishes, a table, three or four chairs, and the beds.",
  },
  {
    id: "usher-opening",
    topic: "stories",
    author: "Edgar Allan Poe",
    work: "The Fall of the House of Usher",
    year: 1839,
    excerpt: true,
    source: "Project Gutenberg #932",
    url: "https://www.gutenberg.org/ebooks/932",
    text: "During the whole of a dull, dark, and soundless day in the autumn of the year, when the clouds hung oppressively low in the heavens, I had been passing alone, on horseback, through a singularly dreary tract of country; and at length found myself, as the shades of the evening drew on, within view of the melancholy House of Usher.",
  },
  {
    id: "usher-gloom",
    topic: "stories",
    author: "Edgar Allan Poe",
    work: "The Fall of the House of Usher",
    year: 1839,
    excerpt: false,
    source: "Project Gutenberg #932",
    url: "https://www.gutenberg.org/ebooks/932",
    text: "I know not how it was—but, with the first glimpse of the building, a sense of insufferable gloom pervaded my spirit. I say insufferable; for the feeling was unrelieved by any of that half-pleasureable, because poetic, sentiment, with which the mind usually receives even the sternest natural images of the desolate or terrible. I looked upon the scene before me—upon the mere house, and the simple landscape features of the domain—upon the bleak walls—upon the vacant eye-like windows—upon a few rank sedges—and upon a few white trunks of decayed trees—with an utter depression of soul which I can compare to no earthly sensation more properly than to the after-dream of the reveller upon opium—the bitter lapse into everyday life—the hideous dropping off of the veil.",
  },
  {
    id: "scarlet-letter",
    topic: "stories",
    author: "Nathaniel Hawthorne",
    work: "The Scarlet Letter",
    year: 1850,
    excerpt: true,
    source: "Project Gutenberg #33",
    url: "https://www.gutenberg.org/ebooks/33",
    text: "A throng of bearded men, in sad-coloured garments and grey steeple-crowned hats, intermixed with women, some wearing hoods, and others bareheaded, was assembled in front of a wooden edifice, the door of which was heavily timbered with oak, and studded with iron spikes.",
  },
  {
    id: "tom-sawyer-law",
    topic: "stories",
    author: "Mark Twain",
    work: "The Adventures of Tom Sawyer",
    year: 1876,
    excerpt: true,
    source: "Project Gutenberg #74",
    url: "https://www.gutenberg.org/ebooks/74",
    text: "Tom said to himself that it was not such a hollow world, after all. He had discovered a great law of human action, without knowing it—namely, that in order to make a man or a boy covet a thing, it is only necessary to make the thing difficult to attain.",
  },
  {
    id: "gift-of-the-magi",
    topic: "stories",
    author: "O. Henry",
    work: "The Gift of the Magi",
    year: 1905,
    excerpt: true,
    source: "Project Gutenberg #7256",
    url: "https://www.gutenberg.org/ebooks/7256",
    text: "One dollar and eighty-seven cents. That was all. And sixty cents of it was in pennies. Pennies saved one and two at a time by bulldozing the grocer and the vegetable man and the butcher until one's cheeks burned with the silent imputation of parsimony that such close dealing implied.",
  },
  {
    id: "o-pioneers",
    topic: "stories",
    author: "Willa Cather",
    work: "O Pioneers!",
    year: 1913,
    excerpt: true,
    source: "Project Gutenberg #24",
    url: "https://www.gutenberg.org/ebooks/24",
    text: "One January day, thirty years ago, the little town of Hanover, anchored on a windy Nebraska tableland, was trying not to be blown away. A mist of fine snowflakes was curling and eddying about the cluster of low drab buildings huddled on the gray prairie, under a gray sky. The dwelling-houses were set about haphazard on the tough prairie sod; some of them looked as if they had been moved in overnight, and others as if they were straying off by themselves, headed straight for the open plain. None of them had any appearance of permanence, and the howling wind blew under them as well as over them.",
  },
  {
    id: "secret-garden",
    topic: "stories",
    author: "Frances Hodgson Burnett",
    work: "The Secret Garden",
    year: 1911,
    excerpt: false,
    source: "Project Gutenberg #113",
    url: "https://www.gutenberg.org/ebooks/113",
    text: "When Mary Lennox was sent to Misselthwaite Manor to live with her uncle everybody said she was the most disagreeable-looking child ever seen. It was true, too. She had a little thin face and a little thin body, thin light hair and a sour expression.",
  },
  {
    id: "little-women",
    topic: "stories",
    author: "Louisa May Alcott",
    work: "Little Women",
    year: 1868,
    excerpt: true,
    source: "Project Gutenberg #514",
    url: "https://www.gutenberg.org/ebooks/514",
    text: '"Christmas won\'t be Christmas without any presents," grumbled Jo, lying on the rug. "It\'s so dreadful to be poor!" sighed Meg, looking down at her old dress. "I don\'t think it\'s fair for some girls to have plenty of pretty things, and other girls nothing at all," added little Amy, with an injured sniff.',
  },
  {
    id: "walden-desperation",
    topic: "ideas",
    author: "Henry David Thoreau",
    work: "Walden",
    year: 1854,
    excerpt: true,
    source: "Project Gutenberg #205",
    url: "https://www.gutenberg.org/ebooks/205",
    text: "The mass of men lead lives of quiet desperation. What is called resignation is confirmed desperation. From the desperate city you go into the desperate country, and have to console yourself with the bravery of minks and muskrats. A stereotyped but unconscious despair is concealed even under what are called the games and amusements of mankind. There is no play in them, for this comes after work. But it is a characteristic of wisdom not to do desperate things.",
  },
  {
    id: "walden-woods",
    topic: "ideas",
    author: "Henry David Thoreau",
    work: "Walden",
    year: 1854,
    excerpt: false,
    source: "Project Gutenberg #205",
    url: "https://www.gutenberg.org/ebooks/205",
    text: "I went to the woods because I wished to live deliberately, to front only the essential facts of life, and see if I could not learn what it had to teach, and not, when I came to die, discover that I had not lived. I did not wish to live what was not life, living is so dear; nor did I wish to practise resignation, unless it was quite necessary. I wanted to live deep and suck out all the marrow of life, to live so sturdily and Spartan-like as to put to rout all that was not life, to cut a broad swath and shave close, to drive life into a corner, and reduce it to its lowest terms, and, if it proved to be mean, why then to get the whole and genuine meanness of it, and publish its meanness to the world; or if it were sublime, to know it by experience, and be able to give a true account of it in my next excursion.",
  },
  {
    id: "emerson-time",
    topic: "ideas",
    author: "Ralph Waldo Emerson",
    work: "Self-Reliance",
    year: 1841,
    excerpt: true,
    source: "Project Gutenberg #2944",
    url: "https://www.gutenberg.org/ebooks/2944",
    text: "There is a time in every man's education when he arrives at the conviction that envy is ignorance; that imitation is suicide; that he must take himself for better for worse as his portion; that though the wide universe is full of good, no kernel of nourishing corn can come to him but through his toil bestowed on that plot of ground which is given to him to till.",
  },
  {
    id: "emerson-misunderstood",
    topic: "ideas",
    author: "Ralph Waldo Emerson",
    work: "Self-Reliance",
    year: 1841,
    excerpt: true,
    source: "Project Gutenberg #2944",
    url: "https://www.gutenberg.org/ebooks/2944",
    text: "Is it so bad then to be misunderstood? Pythagoras was misunderstood, and Socrates, and Jesus, and Luther, and Copernicus, and Galileo, and Newton, and every pure and wise spirit that ever took flesh. To be great is to be misunderstood.",
  },
  {
    id: "emerson-nonconformist",
    topic: "ideas",
    author: "Ralph Waldo Emerson",
    work: "Self-Reliance",
    year: 1841,
    excerpt: true,
    source: "Project Gutenberg #2944",
    url: "https://www.gutenberg.org/ebooks/2944",
    text: "Whoso would be a man, must be a nonconformist. He who would gather immortal palms must not be hindered by the name of goodness, but must explore if it be goodness. Nothing is at last sacred but the integrity of your own mind. Absolve you to yourself, and you shall have the suffrage of the world.",
  },
  {
    id: "james-habit",
    topic: "ideas",
    author: "William James",
    work: "The Principles of Psychology",
    year: 1890,
    excerpt: true,
    source: "Project Gutenberg #57628",
    url: "https://www.gutenberg.org/ebooks/57628",
    text: "Habit is thus the enormous fly-wheel of society, its most precious conservative agent. It alone is what keeps us all within the bounds of ordinance, and saves the children of fortune from the envious uprisings of the poor. It alone prevents the hardest and most repulsive walks of life from being deserted by those brought up to tread therein. It keeps the fisherman and the deck-hand at sea through the winter; it holds the miner in his darkness, and nails the countryman to his log-cabin and his lonely farm through all the months of snow; it protects us from invasion by the natives of the desert and the frozen zone. It dooms us all to fight out the battle of life upon the lines of our nurture or our early choice, and to make the best of a pursuit that disagrees, because there is no other for which we are fitted, and it is too late to begin again.",
  },
  {
    id: "dubois-question",
    topic: "ideas",
    author: "W. E. B. Du Bois",
    work: "The Souls of Black Folk",
    year: 1903,
    excerpt: true,
    source: "Project Gutenberg #408",
    url: "https://www.gutenberg.org/ebooks/408",
    text: "Between me and the other world there is ever an unasked question: unasked by some through feelings of delicacy; by others through the difficulty of rightly framing it. All, nevertheless, flutter round it. They approach me in a half-hesitant sort of way, eye me curiously or compassionately, and then, instead of saying directly, How does it feel to be a problem? they say, I know an excellent colored man in my town; or, I fought at Mechanicsville; or, Do not these Southern outrages make your blood boil? At these I smile, or am interested, or reduce the boiling to a simmer, as the occasion may require. To the real question, How does it feel to be a problem? I answer seldom a word.",
  },
  {
    id: "roosevelt-arena",
    topic: "ideas",
    author: "Theodore Roosevelt",
    work: "Citizenship in a Republic",
    year: 1910,
    excerpt: true,
    source: "Wikisource",
    url: "https://en.wikisource.org/wiki/Citizenship_in_a_Republic",
    text: "It is not the critic who counts; not the man who points out how the strong man stumbles, or where the doer of deeds could have done them better. The credit belongs to the man who is actually in the arena, whose face is marred by dust and sweat and blood; who strives valiantly; who errs, who comes short again and again, because there is no effort without error and shortcoming; but who does actually strive to do the deeds; who knows great enthusiasms, the great devotions; who spends himself in a worthy cause; who at the best knows in the end the triumph of high achievement, and who at the worst, if he fails, at least fails while daring greatly, so that his place shall never be with those cold and timid souls who neither know victory nor defeat.",
  },
  {
    id: "washington-bucket",
    topic: "history",
    author: "Booker T. Washington",
    work: "Up from Slavery",
    year: 1901,
    excerpt: true,
    source: "Project Gutenberg #2376",
    url: "https://www.gutenberg.org/ebooks/2376",
    text: 'A ship lost at sea for many days suddenly sighted a friendly vessel. From the mast of the unfortunate vessel was seen a signal, "Water, water; we die of thirst!" The answer from the friendly vessel at once came back, "Cast down your bucket where you are." A second time the signal, "Water, water; send us water!" ran up from the distressed vessel, and was answered, "Cast down your bucket where you are." And a third and fourth signal for water was answered, "Cast down your bucket where you are." The captain of the distressed vessel, at last heeding the injunction, cast down his bucket, and it came up full of fresh, sparkling water from the mouth of the Amazon River. To those of my race who depend on bettering their condition in a foreign land or who underestimate the importance of cultivating friendly relations with the Southern white man, who is their next-door neighbour, I would say: "Cast down your bucket where you are"—cast it down in making friends in every manly way of the people of all races by whom we are surrounded.',
  },
  {
    id: "declaration-preamble",
    topic: "history",
    author: "Thomas Jefferson and the Continental Congress",
    work: "The Declaration of Independence",
    year: 1776,
    excerpt: false,
    source: "Project Gutenberg #1",
    url: "https://www.gutenberg.org/ebooks/1",
    text: "When in the Course of human events, it becomes necessary for one people to dissolve the political bands which have connected them with another, and to assume, among the Powers of the earth, the separate and equal station to which the Laws of Nature and of Nature's God entitle them, a decent respect to the opinions of mankind requires that they should declare the causes which impel them to the separation.",
  },
  {
    id: "declaration-truths",
    topic: "history",
    author: "Thomas Jefferson and the Continental Congress",
    work: "The Declaration of Independence",
    year: 1776,
    excerpt: true,
    source: "Project Gutenberg #1",
    url: "https://www.gutenberg.org/ebooks/1",
    text: "We hold these truths to be self-evident, that all men are created equal, that they are endowed by their Creator with certain unalienable Rights, that among these are Life, Liberty, and the pursuit of Happiness.",
  },
  {
    id: "lincoln-malice",
    topic: "history",
    author: "Abraham Lincoln",
    work: "Second Inaugural Address",
    year: 1865,
    excerpt: true,
    source: "Project Gutenberg #925",
    url: "https://www.gutenberg.org/ebooks/925",
    text: "With malice toward none, with charity for all, with firmness in the right as God gives us to see the right, let us strive on to finish the work we are in, to bind up the nation's wounds, to care for him who shall have borne the battle and for his widow and his orphan, to do all which may achieve and cherish a just and lasting peace among ourselves and with all nations.",
  },
  {
    id: "lincoln-fondly",
    topic: "history",
    author: "Abraham Lincoln",
    work: "Second Inaugural Address",
    year: 1865,
    excerpt: true,
    source: "Project Gutenberg #925",
    url: "https://www.gutenberg.org/ebooks/925",
    text: "Fondly do we hope, fervently do we pray, that this mighty scourge of war may speedily pass away. Yet, if God wills that it continue until all the wealth piled by the bondsman's two hundred and fifty years of unrequited toil shall be sunk, and until every drop of blood drawn with the lash shall be paid by another drawn with the sword, as was said three thousand years ago, so still it must be said \"the judgments of the Lord are true and righteous altogether.",
  },
  {
    id: "lincoln-chords",
    topic: "history",
    author: "Abraham Lincoln",
    work: "First Inaugural Address",
    year: 1861,
    excerpt: true,
    source: "Project Gutenberg #925",
    url: "https://www.gutenberg.org/ebooks/925",
    text: "I am loath to close. We are not enemies, but friends. We must not be enemies. Though passion may have strained it must not break our bonds of affection. The mystic chords of memory, stretching from every battlefield and patriot grave to every living heart and hearthstone all over this broad land, will yet swell the chorus of the Union, when again touched, as surely they will be, by the better angels of our nature.",
  },
  {
    id: "constitution-preamble",
    topic: "history",
    author: "The Constitutional Convention",
    work: "The Constitution of the United States",
    year: 1787,
    excerpt: false,
    source: "Wikisource",
    url: "https://en.wikisource.org/wiki/Constitution_of_the_United_States_of_America",
    text: "We the People of the United States, in Order to form a more perfect Union, establish Justice, insure domestic Tranquility, provide for the common defence, promote the general Welfare, and secure the Blessings of Liberty to ourselves and our Posterity, do ordain and establish this Constitution for the United States of America.",
  },
  {
    id: "gettysburg",
    topic: "history",
    author: "Abraham Lincoln",
    work: "The Gettysburg Address (Bliss copy)",
    year: 1863,
    excerpt: false,
    source: "Wikisource",
    url: "https://en.wikisource.org/wiki/Gettysburg_Address_(Bliss_copy)",
    text: "Four score and seven years ago our fathers brought forth, on this continent, a new nation, conceived in Liberty, and dedicated to the proposition that all men are created equal. Now we are engaged in a great civil war, testing whether that nation, or any nation so conceived and so dedicated, can long endure. We are met on a great battle-field of that war. We have come to dedicate a portion of that field, as a final resting place for those who here gave their lives that that nation might live. It is altogether fitting and proper that we should do this. But, in a larger sense, we can not dedicate—we can not consecrate—we can not hallow—this ground. The brave men, living and dead, who struggled here, have consecrated it, far above our poor power to add or detract. The world will little note, nor long remember what we say here, but it can never forget what they did here. It is for us the living, rather, to be dedicated here to the unfinished work which they who fought here have thus far so nobly advanced. It is rather for us to be here dedicated to the great task remaining before us—that from these honored dead we take increased devotion to that cause for which they gave the last full measure of devotion—that we here highly resolve that these dead shall not have died in vain—that this nation, under God, shall have a new birth of freedom—and that government of the people, by the people, for the people, shall not perish from the earth.",
  },
  {
    id: "douglass-fourth-of-july",
    topic: "history",
    author: "Frederick Douglass",
    work: "What to the Slave Is the Fourth of July?",
    year: 1852,
    excerpt: true,
    source: "Wikisource",
    url: "https://en.wikisource.org/wiki/What_to_the_Slave_Is_the_Fourth_of_July%3F",
    text: "Fellow-citizens, pardon me, allow me to ask, why am I called upon to speak here to-day? What have I, or those I represent, to do with your national independence? Are the great principles of political freedom and of natural justice, embodied in that Declaration of Independence, extended to us? and am I, therefore, called upon to bring our humble offering to the national altar, and to confess the benefits and express devout gratitude for the blessings resulting from your independence to us? Would to God, both for your sakes and ours, that an affirmative answer could be truthfully returned to these questions! Then would my task be light, and my burden easy and delightful.",
  },
];
